import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode, Transition as _Transition, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "seller__side" }
const _hoisted_2 = { class: "seller__brand" }
const _hoisted_3 = { class: "seller__brand-mark" }
const _hoisted_4 = { class: "seller__brand-text" }
const _hoisted_5 = { class: "seller__store" }
const _hoisted_6 = { class: "seller__store-name" }
const _hoisted_7 = { class: "seller__store-meta" }
const _hoisted_8 = { class: "seller__grade" }
const _hoisted_9 = {
  class: "seller__nav",
  "aria-label": "판매관리센터 메뉴"
}
const _hoisted_10 = { class: "seller__nav-title" }
const _hoisted_11 = {
  key: 0,
  class: "seller__nav-badge"
}
const _hoisted_12 = { class: "seller__side-foot" }
const _hoisted_13 = ["href"]
const _hoisted_14 = { class: "seller__body" }
const _hoisted_15 = {
  key: 0,
  class: "seller__impersonate",
  "data-tour": "seller-viewing-as-admin"
}
const _hoisted_16 = { class: "seller__top" }
const _hoisted_17 = ["aria-expanded"]
const _hoisted_18 = {
  class: "seller__top-title",
  "data-tour": "page-title"
}
const _hoisted_19 = { key: 0 }
const _hoisted_20 = { class: "seller__top-right" }
const _hoisted_21 = { class: "seller__today" }
const _hoisted_22 = ["aria-expanded"]
const _hoisted_23 = {
  key: 0,
  class: "seller__alert-dot"
}
const _hoisted_24 = {
  key: 0,
  class: "seller__alert-panel"
}
const _hoisted_25 = {
  key: 0,
  class: "seller__alert-empty"
}
const _hoisted_26 = ["onClick"]
const _hoisted_27 = {
  key: 0,
  class: "seller__notify-order"
}
const _hoisted_28 = { key: 1 }
const _hoisted_29 = {
  key: 1,
  class: "seller__alert-empty"
}
const _hoisted_30 = { class: "seller__user" }
const _hoisted_31 = { class: "seller__avatar" }
const _hoisted_32 = { class: "seller__user-text" }
const _hoisted_33 = { class: "seller__main" }
const _hoisted_34 = {
  key: 0,
  class: "seller__pending"
}
const _hoisted_35 = {
  key: 0,
  class: "sui-toast"
}

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import FeatureSpecPanel from '@/components/manage/FeatureSpecPanel.vue'
import FeatureTourOverlay from '@/components/manage/FeatureTourOverlay.vue'
import { MALL_HOME_PATH, MALL_NAME } from '@/constants/mall'
import { SELLER_GRADE_LABEL, SELLER_STATUS_LABEL } from '@/constants/sellerSession'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { useManageUiScale } from '@/composables/useManageUiScale'
import { useSellerData } from '@/composables/useSellerData'
import { fetchSellerOrders } from '@/api/mallOrder'
import { fetchSellerQnas } from '@/api/mallReview'
import {
  fetchSellerNotifies,
  markSellerNotifiesRead,
  type SellerNotifyItem,
} from '@/api/seller'

interface NavItem {
  label: string
  to: string
  icon: string
  badge?: number
  exact?: boolean
  tour?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SellerLayout',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const { seller, isPending, isViewingAsAdmin, logout } = useSellerAuth()
const { scaleStyle } = useManageUiScale()
/* 주문 화면 등에서 올리는 토스트는 레이아웃이 받아서 보여 준다 */
const { toast } = useSellerData()

const mustOnboard = computed(() => Boolean(seller.value?.mustOnboard))
const viewingAdminLabel = computed(() => {
  const view = seller.value?.viewingAsAdmin
  if (!view) {
    return ''
  }
  return view.adminName || view.adminId
})
const storeName = computed(() => seller.value?.storeName ?? '판매업체')
const bizName = computed(() => seller.value?.bizName ?? '')
const storeInitial = computed(() => storeName.value.slice(0, 1) || '몰')
const gradeLabel = computed(() => SELLER_GRADE_LABEL[seller.value?.grade ?? 'NORMAL'] ?? '일반')
const statusLabel = computed(() =>
  seller.value ? SELLER_STATUS_LABEL[seller.value.status] : '확인 중',
)

const navOpen = ref(false)
const alertOpen = ref(false)
const alertRef = ref<HTMLElement | null>(null)

const newOrders = ref(0)
const readyOrders = ref(0)
const claimOrders = ref(0)
const openInquiries = ref(0)
const recentNotifies = ref<SellerNotifyItem[]>([])
const unreadNotifies = ref(0)

async function loadBadges() {
  if (mustOnboard.value) {
    newOrders.value = 0
    readyOrders.value = 0
    claimOrders.value = 0
    openInquiries.value = 0
    recentNotifies.value = []
    unreadNotifies.value = 0
    return
  }
  try {
    const [orders, qnas, notifies] = await Promise.all([
      fetchSellerOrders().catch(() => null),
      fetchSellerQnas().catch(() => []),
      fetchSellerNotifies().catch(() => null),
    ])
    newOrders.value = orders?.counts.PAID ?? 0
    readyOrders.value = orders?.counts.READY ?? 0
    claimOrders.value =
      (orders?.counts.CANCEL_REQUEST ?? 0) + (orders?.counts.RETURN_REQUEST ?? 0)
    openInquiries.value = qnas.filter((item) => !item.answer).length
    recentNotifies.value = (notifies?.items || []).slice(0, 8)
    unreadNotifies.value = notifies?.unread ?? 0
  } catch {
    /* 배지는 실패해도 화면은 살아 있어야 한다 */
  }
}

function notifyLink(item: SellerNotifyItem): string {
  const code = String(item.orderCode || '').trim()
  if (item.type === 'CLAIM' || item.type === 'SELLER_CLAIM') {
    return code
      ? `/seller/claims?keyword=${encodeURIComponent(code)}`
      : '/seller/claims'
  }
  if (item.type === 'SELLER_INQUIRY' || item.type === 'INQUIRY') {
    return code
      ? `/seller/reviews?tab=inquiry&filter=open&keyword=${encodeURIComponent(code)}`
      : '/seller/reviews?tab=inquiry&filter=open'
  }
  if (code) {
    return `/seller/orders?keyword=${encodeURIComponent(code)}`
  }
  return '/seller/orders'
}

async function openNotify(item: SellerNotifyItem) {
  alertOpen.value = false
  try {
    if (!item.read) {
      unreadNotifies.value = await markSellerNotifiesRead(item.idx)
      item.read = true
    }
  } catch {
    /* 이동은 계속 */
  }
  router.push(notifyLink(item))
}

async function onToggleAlerts() {
  alertOpen.value = !alertOpen.value
  if (!alertOpen.value || !unreadNotifies.value) return
  try {
    unreadNotifies.value = await markSellerNotifiesRead()
    recentNotifies.value = recentNotifies.value.map((row) => ({ ...row, read: true }))
  } catch {
    /* 읽음 실패해도 목록은 유지 */
  }
}



const navGroups = computed<Array<{ title: string; items: NavItem[] }>>(() => {
  if (mustOnboard.value) {
    return [
      {
        title: '입점 준비',
        items: [
          {
            label: '온보딩',
            to: '/seller/onboarding',
            icon: 'mdi-clipboard-check-outline',
            exact: true,
          },
        ],
      },
    ]
  }
  return [
  {
    title: '판매 현황',
    items: [{ label: '대시보드', to: '/seller', icon: 'mdi-view-dashboard-outline', exact: true }],
  },
  {
    title: '주문 · 배송',
    items: [
      {
        label: '주문 관리',
        to: '/seller/orders',
        icon: 'mdi-clipboard-list-outline',
        badge: newOrders.value + readyOrders.value,
      },
      { label: '취소 · 반품', to: '/seller/claims', icon: 'mdi-package-variant-remove', badge: claimOrders.value },
    ],
  },
  {
    title: '상품',
    items: [
      {
        label: '상품 관리',
        to: '/seller/products',
        icon: 'mdi-package-variant-closed',
        tour: 'seller-products-nav',
      },
    ],
  },
  {
    title: '고객 · 정산',
    items: [
      { label: '리뷰 · 문의', to: '/seller/reviews', icon: 'mdi-comment-text-outline', badge: openInquiries.value },
      { label: '정산 관리', to: '/seller/settlements', icon: 'mdi-cash-multiple' },
    ],
  },
  {
    title: '설정',
    items: [{ label: '판매업체 정보', to: '/seller/settings', icon: 'mdi-cog-outline' }],
  },
]
})

const alerts = computed(() =>
  [
    { label: '신규 주문 확인', count: newOrders.value, to: '/seller/orders?status=PAID', icon: 'mdi-cart-arrow-down' },
    { label: '발송 대기', count: readyOrders.value, to: '/seller/orders?status=READY', icon: 'mdi-truck-outline' },
    { label: '취소 · 반품 요청', count: claimOrders.value, to: '/seller/claims', icon: 'mdi-alert-outline' },
    { label: '미답변 문의', count: openInquiries.value, to: '/seller/reviews?tab=inquiry&filter=open', icon: 'mdi-help-circle-outline' },
  ].filter((alert) => alert.count > 0),
)

const bellCount = computed(() => alerts.value.length + unreadNotifies.value)

const pageTitle = computed(() => (route.meta.sellerTitle as string) ?? '판매관리센터')
const pageDesc = computed(() => (route.meta.sellerDesc as string) ?? '')

const today = new Date().toLocaleDateString('ko-KR', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  weekday: 'short',
})

function isActive(item: NavItem): boolean {
  const [itemPath, itemQuery = ''] = item.to.split('?')

  if (itemQuery) {
    if (route.path !== itemPath) return false
    const want = new URLSearchParams(itemQuery)
    return [...want.entries()].every(([key, value]) => String(route.query[key] ?? '') === value)
  }

  if (item.exact) return route.path === item.to

  if (route.path === itemPath) return true
  if (!route.path.startsWith(`${itemPath}/`)) return false

  const rivals = navGroups.value
    .flatMap((group) => group.items)
    .filter((other) => {
      const otherPath = other.to.split('?')[0]
      return other.to !== item.to && otherPath.startsWith(`${itemPath}/`)
    })
  if (rivals.some((other) => {
    const otherPath = other.to.split('?')[0]
    return route.path === otherPath || route.path.startsWith(`${otherPath}/`)
  })) {
    return false
  }
  return true
}

async function onExitView() {
  await logout()
  await router.push({ name: 'admin-seller-view' })
}

async function onLogout() {
  if (isViewingAsAdmin.value) {
    if (!window.confirm('판매업체 보기를 끝내고 관리자 화면으로 돌아갈까요?')) {
      return
    }
    await onExitView()
    return
  }

  if (!window.confirm('판매관리센터에서 로그아웃할까요?')) {
    return
  }

  await logout()

  /* 새 창으로 연 경우엔 그대로 닫고, 직접 주소로 들어왔다면 로그인 화면으로 돌려보낸다. */
  window.close()
  window.setTimeout(() => {
    if (!window.closed) {
      router.push({ path: '/login', query: { tab: 'seller' } })
    }
  }, 120)
}

function onDocClick(event: MouseEvent) {
  if (!alertOpen.value) return
  if (alertRef.value?.contains(event.target as Node)) return
  alertOpen.value = false
}

watch(
  () => route.fullPath,
  () => {
    navOpen.value = false
    alertOpen.value = false
    loadBadges()
  },
)

onMounted(() => {
  loadBadges()
  document.addEventListener('click', onDocClick)
})
onUnmounted(() => document.removeEventListener('click', onDocClick))

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["seller", { 'seller--nav-open': navOpen.value }]),
    style: _normalizeStyle(_unref(scaleStyle))
  }, [
    _createElementVNode("aside", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("span", _hoisted_3, [
          _createVNode(AppIcon, {
            icon: "mdi-storefront",
            size: 22
          })
        ]),
        _createElementVNode("span", _hoisted_4, [
          _createElementVNode("strong", null, _toDisplayString(_unref(MALL_NAME)), 1),
          _cache[4] || (_cache[4] = _createElementVNode("em", null, "판매관리센터", -1))
        ])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("p", _hoisted_6, _toDisplayString(storeName.value), 1),
        _createElementVNode("p", _hoisted_7, [
          _createElementVNode("span", _hoisted_8, _toDisplayString(gradeLabel.value), 1),
          _createElementVNode("span", null, _toDisplayString(bizName.value), 1)
        ])
      ]),
      _createElementVNode("nav", _hoisted_9, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(navGroups.value, (group) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: group.title
          }, [
            _createElementVNode("p", _hoisted_10, _toDisplayString(group.title), 1),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.items, (item) => {
              return (_openBlock(), _createBlock(_component_router_link, {
                key: item.label,
                to: item.to,
                class: _normalizeClass(["seller__nav-link", { 'is-active': isActive(item) }]),
                "data-tour": item.tour,
                onClick: _cache[0] || (_cache[0] = ($event: any) => (navOpen.value = false))
              }, {
                default: _withCtx(() => [
                  _createVNode(AppIcon, {
                    icon: item.icon,
                    size: 19
                  }, null, 8, ["icon"]),
                  _createElementVNode("span", null, _toDisplayString(item.label), 1),
                  (item.badge)
                    ? (_openBlock(), _createElementBlock("em", _hoisted_11, _toDisplayString(item.badge), 1))
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1032, ["to", "class", "data-tour"]))
            }), 128))
          ], 64))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("a", {
          class: "seller__side-link",
          href: _unref(MALL_HOME_PATH),
          target: "_blank",
          rel: "noopener"
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-open-in-new",
            size: 17
          }),
          _cache[5] || (_cache[5] = _createTextVNode(" 함우리몰 보기 ", -1))
        ], 8, _hoisted_13),
        _createElementVNode("button", {
          type: "button",
          class: "seller__side-link",
          onClick: onLogout
        }, [
          _createVNode(AppIcon, {
            icon: _unref(isViewingAsAdmin) ? 'mdi-arrow-left' : 'mdi-logout-variant',
            size: 17
          }, null, 8, ["icon"]),
          _createTextVNode(" " + _toDisplayString(_unref(isViewingAsAdmin) ? '관리자로 돌아가기' : '로그아웃'), 1)
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_14, [
      (_unref(isViewingAsAdmin))
        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
            _createVNode(AppIcon, {
              icon: "mdi-shield-account-outline",
              size: 18
            }),
            _createElementVNode("span", null, [
              _createTextVNode(" 최고관리자(" + _toDisplayString(viewingAdminLabel.value) + ")로 ", 1),
              _createElementVNode("strong", null, _toDisplayString(storeName.value), 1),
              _createTextVNode(" (" + _toDisplayString(_unref(seller)?.sellerId) + ") 화면을 보고 있습니다. ", 1)
            ]),
            _createElementVNode("button", {
              type: "button",
              class: "seller__impersonate-exit",
              onClick: onExitView
            }, " 관리자로 돌아가기 ")
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("header", _hoisted_16, [
        _createElementVNode("button", {
          type: "button",
          class: "seller__nav-toggle",
          "aria-expanded": navOpen.value,
          "aria-label": "메뉴 열기",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (navOpen.value = !navOpen.value))
        }, [
          _createVNode(AppIcon, {
            icon: navOpen.value ? 'mdi-close' : 'mdi-menu',
            size: 22
          }, null, 8, ["icon"])
        ], 8, _hoisted_17),
        _createElementVNode("div", _hoisted_18, [
          _createElementVNode("h1", null, _toDisplayString(pageTitle.value), 1),
          (pageDesc.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(pageDesc.value), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_20, [
          _createVNode(FeatureSpecPanel),
          _createElementVNode("span", _hoisted_21, _toDisplayString(_unref(today)), 1),
          _createElementVNode("div", {
            class: "seller__alerts",
            ref_key: "alertRef",
            ref: alertRef,
            "data-tour": "seller-notify-panel"
          }, [
            _createElementVNode("button", {
              type: "button",
              class: "seller__icon-btn",
              "aria-expanded": alertOpen.value,
              "aria-label": "알림",
              onClick: onToggleAlerts
            }, [
              _createVNode(AppIcon, {
                icon: "mdi-bell-outline",
                size: 20
              }),
              (bellCount.value)
                ? (_openBlock(), _createElementBlock("em", _hoisted_23, _toDisplayString(bellCount.value), 1))
                : _createCommentVNode("", true)
            ], 8, _hoisted_22),
            _createVNode(_Transition, { name: "seller-pop" }, {
              default: _withCtx(() => [
                (alertOpen.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                      _cache[6] || (_cache[6] = _createElementVNode("p", { class: "seller__alert-head" }, "처리가 필요한 일", -1)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(alerts.value, (alert) => {
                        return (_openBlock(), _createBlock(_component_router_link, {
                          key: alert.label,
                          to: alert.to,
                          class: "seller__alert-item",
                          onClick: _cache[2] || (_cache[2] = ($event: any) => (alertOpen.value = false))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(AppIcon, {
                              icon: alert.icon,
                              size: 18
                            }, null, 8, ["icon"]),
                            _createElementVNode("span", null, _toDisplayString(alert.label), 1),
                            _createElementVNode("strong", null, _toDisplayString(alert.count), 1)
                          ]),
                          _: 2
                        }, 1032, ["to"]))
                      }), 128)),
                      (!alerts.value.length)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_25, "지금은 처리할 일이 없습니다."))
                        : _createCommentVNode("", true),
                      _cache[7] || (_cache[7] = _createElementVNode("p", { class: "seller__alert-head seller__alert-head--sub" }, "최근 알림", -1)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recentNotifies.value, (item) => {
                        return (_openBlock(), _createElementBlock("button", {
                          key: item.idx,
                          type: "button",
                          class: _normalizeClass(["seller__notify-item", { 'is-unread': !item.read }]),
                          onClick: ($event: any) => (openNotify(item))
                        }, [
                          _createElementVNode("strong", null, _toDisplayString(item.title), 1),
                          (item.orderCode)
                            ? (_openBlock(), _createElementBlock("em", _hoisted_27, _toDisplayString(item.orderCode), 1))
                            : _createCommentVNode("", true),
                          (item.body)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_28, _toDisplayString(item.body), 1))
                            : _createCommentVNode("", true)
                        ], 10, _hoisted_26))
                      }), 128)),
                      (!recentNotifies.value.length)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_29, "새 알림이 없습니다."))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ], 512),
          _createElementVNode("div", _hoisted_30, [
            _createElementVNode("span", _hoisted_31, _toDisplayString(storeInitial.value), 1),
            _createElementVNode("span", _hoisted_32, [
              _createElementVNode("strong", null, _toDisplayString(storeName.value), 1),
              _createElementVNode("em", null, _toDisplayString(statusLabel.value), 1)
            ])
          ])
        ])
      ]),
      _createVNode(FeatureTourOverlay),
      _createElementVNode("main", _hoisted_33, [
        (_unref(isPending) && !mustOnboard.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_34, [
              _createVNode(AppIcon, {
                icon: "mdi-clock-outline",
                size: 20
              }),
              _cache[8] || (_cache[8] = _createElementVNode("span", null, [
                _createElementVNode("strong", null, "승인 심사가 진행 중입니다."),
                _createTextVNode(" 온보딩·사업자 정보를 확인하는 동안 화면을 미리 둘러볼 수 있습니다. 관리자 승인이 완료되면 상품 등록과 판매가 열립니다. ")
              ], -1))
            ]))
          : _createCommentVNode("", true),
        _renderSlot(_ctx.$slots, "default")
      ])
    ]),
    (navOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "seller__scrim",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (navOpen.value = false))
        }))
      : _createCommentVNode("", true),
    _createVNode(_Transition, { name: "sui-toast" }, {
      default: _withCtx(() => [
        (_unref(toast))
          ? (_openBlock(), _createElementBlock("p", _hoisted_35, [
              _createVNode(AppIcon, {
                icon: "mdi-check-circle",
                size: 18
              }),
              _createTextVNode(" " + _toDisplayString(_unref(toast)), 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 6))
}
}

})